"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import { useNavigationReady } from "../contexts/NavigationReadyContext";
import Footer from "./marketing/Footer";
import { isLandingPath } from "@/lib/landing-paths";

/**
 * Компонент, который откладывает рендеринг контента страницы
 * до тех пор, пока на navigation не будет готова (проверка авторизации завершена).
 * 
 * Это обеспечивает, что пользователь сначала видит навигацию с правильным
 * статусом авторизации, а затем уже контент страницы.
 */
export default function NavigationAwareContent({ children }: { children: React.ReactNode }) {
  const { isNavigationReady } = useNavigationReady();
  const pathname = usePathname();

  // Full reload can restore scrollY after React's first paint (footer visible on
  // /stock/*). Manual restoration + reset after nav/content mount fixes it.
  useLayoutEffect(() => {
    if (isLandingPath(pathname)) return;
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, [pathname]);

  useLayoutEffect(() => {
    if (isLandingPath(pathname)) return;
    if (!isNavigationReady) return;
    window.scrollTo(0, 0);
  }, [pathname, isNavigationReady]);

  // App Router client navigations can leave window.scrollY from the previous
  // page (e.g. Hub scrolled to Bastion Portfolios at the bottom → Portfolios
  // opens at the same offset). Reset on real route changes. Portfolios tab
  // switches use history.replaceState and do not update usePathname(), so
  // in-page scroll there is unaffected.
  useEffect(() => {
    if (isLandingPath(pathname)) return;
    window.scrollTo(0, 0);
  }, [pathname]);

  const isLandingPage = isLandingPath(pathname);

  const isReportPage = pathname?.startsWith("/reports");
  const isHeadlinesPage = pathname === "/headlines" || pathname?.startsWith("/headlines/");
  const isHubPage = pathname === "/hub";
  const isSupplyChainPage =
    pathname === "/semiconductors-supply-chain" || pathname === "/ai-server-stocks";
  const isNbaPage = pathname === "/nba";
  // Stock pages open with See also under the topnav — keep top padding tiny
  // so the rail sits flush against the header instead of floating in empty air.
  const isStockPage = Boolean(pathname?.startsWith("/stock/"));

  const mainClass = isReportPage
    ? 'pt-0 px-0 pb-4'
    : isNbaPage
      ? 'px-0 pt-0 pb-0 min-w-0'
    : isStockPage
      ? 'px-4 md:px-6 lg:px-8 pt-1 pb-4'
    : isHeadlinesPage
      ? 'px-0 sm:px-3 md:px-6 lg:px-8 py-2 md:py-4'
        : isHubPage || pathname === "/watchlist" || isSupplyChainPage
        ? 'px-0 md:px-6 lg:px-8 py-2 md:py-4'
        : 'px-4 md:px-6 lg:px-8 py-4';

  // Для landing страницы сразу показываем контент без обертки и без Footer
  // (Footer уже включен в landing/page.tsx если нужен)
  // Важно: не добавляем никаких оберток для лендинга
  if (isLandingPage) {
    return <>{children}</>;
  }

  // Always render the page content — including during SSR and the first client
  // paint. Previously this returned an empty placeholder until `isNavigationReady`
  // flipped true on the client, which meant EVERY non-landing page shipped blank
  // HTML to search engines and AI crawlers (all content lived only in the RSC
  // flight payload and required JS to appear). Rendering children on the server
  // puts the real content in the initial HTML. Per-page auth/paywall gating is
  // unchanged: it is enforced by the pages themselves (server-computed access +
  // client refinement), not by hiding the whole DOM here.
  return (
    <>
      <main className={mainClass}>{children}</main>
      {isNbaPage ? null : <Footer />}
    </>
  );
}


